/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {getTestProps} from '@benchling/js-test-utils';
import {Divider, Tabs, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';

import AISettingsGeneral from './AISettingsGeneral';
import AISettingsSkills from './AISettingsSkills';

export const HAS_SEEN_AI_SIDEPANEL_ONBOARDING = 'llm.ai-sidepanel.has-seen-onboarding';

enum AISettingsTab {
  GENERAL = 'GENERAL',
  SKILLS = 'SKILLS',
}

const TAB_LABELS: Record<AISettingsTab, string> = {
  [AISettingsTab.GENERAL]: 'General',
  [AISettingsTab.SKILLS]: 'Skills',
};

export function AISettingsContent({
  chatId,
  onRestartOnboarding,
}: {
  chatId: string | null;
  onRestartOnboarding?: () => void;
}): JSX.Element {
  const {llmEnableSkills: skillsEnabled} = useRolloutFlags();

  const tabs = [AISettingsTab.GENERAL, ...(skillsEnabled ? [AISettingsTab.SKILLS] : [])];

  return (
    <Tabs
      orientation="vertical"
      defaultValue={AISettingsTab.GENERAL}
      styles={{
        root: {
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
        },
        tabsList: {
          width: 160,
          minWidth: 160,
          padding: '16px 8px',
          borderRight: `1px solid ${GRAY_LIGHT}`,
          gap: 8,
        },
        panel: {
          flex: 1,
          padding: '16px 24px',
          overflowY: 'auto',
          minWidth: 0,
        },
      }}
      classNames={{
        panel: 'scrollbar-override',
      }}
    >
      <Tabs.List>
        <Text fw={700} size="sm" py={4} px={8} style={{color: NEAR_BLACK}}>
          AI Chat Settings
        </Text>
        {tabs.map((tab) => (
          <Tabs.Tab
            key={tab}
            value={tab}
            {...getTestProps({
              component: 'AISettings',
              element: 'navItem',
              key: TAB_LABELS[tab],
            })}
          >
            {TAB_LABELS[tab]}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      <Tabs.Panel value={AISettingsTab.GENERAL}>
        <AISettingsGeneral onRestartOnboarding={onRestartOnboarding} />
        {chatId && (
          <>
            <Divider mt={24} />
            <div className={css(styles.exportButtonWrapper)}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                icon="audit-trail"
                onClick={() => {
                  AuditActions.exportAuditLogs([chatId]);
                }}
                size={ButtonSize.SMALL}
                text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
                {...getTestProps({
                  component: 'AISidepanelV2',
                  element: 'exportAuditLogButton',
                })}
              />
            </div>
          </>
        )}
      </Tabs.Panel>

      {skillsEnabled && (
        <Tabs.Panel value={AISettingsTab.SKILLS}>
          <AISettingsSkills />
        </Tabs.Panel>
      )}
    </Tabs>
  );
}

const styles = StyleSheet.create({
  exportButtonWrapper: {
    marginTop: 16,
  },
});
